/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react'
import type { ColDef, ValueSetterParams } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { formatPhoneNumber } from 'react-phone-number-input'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { RosterOfficialRow } from '@sportninja/common/features/competition/operations/utils/rosterRows'
import {
  canEditOfficialEmail,
  type EditableOfficialField,
} from '@sportninja/common/features/competition/operations/utils/officialInlineEdit'
import StatusChip from 'src/components/ts-components/StatusChip'
import PhoneCellEditor from '../components/roster/PhoneCellEditor'
import RosterMemberCell from '../components/roster/RosterMemberCell'
import RosterRowActions, {
  type RosterRowAction,
} from '../components/roster/RosterRowActions'
import { OPERATIONS_PILL_CELL_CLASS } from '../components/operationsGridStyle'

const translate = t as (key: string, options?: unknown) => string

interface Options {
  canEdit: boolean
  officialTypes: Array<{ id: number | string; name_full?: string }>
  /**
   * Inline edit handler. Omitted → read-only cells: the Team screen edits
   * through its Edit Profile modal instead.
   */
  onEditField?: (
    row: RosterOfficialRow,
    field: EditableOfficialField,
    value: string,
    currentValue: string
  ) => void
  onRowAction?: (action: RosterRowAction, row: RosterOfficialRow) => void
  /**
   * Replaces the default ⋯ menu. The Team screen keeps its own menu, which
   * also offers Send Invitation to an official never invited.
   */
  renderActions?: (row: RosterOfficialRow) => ReactNode
}

/** Same scale as the players grid — see `inviteTone` there. */
const inviteTone = (status: RosterOfficialRow['inviteStatus']): 'ok' | 'warn' =>
  status === 'accepted' ? 'ok' : 'warn'

/** '' for never-invited, so the cell stays empty rather than showing a pill. */
const inviteLabel = (status: RosterOfficialRow['inviteStatus']): string => {
  if (status === 'accepted') {
    return translate('common:accepted')
  }
  if (status === 'invited') {
    return translate('common:invited')
  }
  return ''
}

/**
 * Columns for the Team Officials grid — the set the legacy roster already
 * showed (Name, Role, Email, Phone, Invite status) rather than a new one, with
 * Invite moved beside Email.
 *
 * Editable cells commit on blur and each `valueSetter` returns `false`, so AG
 * Grid never writes the row optimistically — the save + `refresh()` is the
 * single source of truth, matching the players grid. Note the PUT behind this
 * is a full replace rather than a diff; `toOfficialUpdatePayload` resends the
 * untouched fields.
 */
export const useRosterOfficialsColumns = ({
  canEdit,
  officialTypes,
  onEditField,
  onRowAction,
  renderActions,
}: Options): ColDef<RosterOfficialRow>[] => {
  'use memo'

  // Cells edit in place only when the caller can save the edit.
  const canEditInline = canEdit && Boolean(onEditField)

  const roleLabel = (typeId: string): string =>
    officialTypes.find((type) => String(type.id) === typeId)?.name_full ?? '-'

  const editCell =
    (
      field: EditableOfficialField,
      currentOf: (row: RosterOfficialRow) => string
    ) =>
    (params: ValueSetterParams<RosterOfficialRow>) => {
      onEditField?.(
        params.data,
        field,
        String(params.newValue ?? ''),
        currentOf(params.data)
      )
      return false
    }

  return [
    {
      colId: 'name',
      field: 'name',
      headerName: capitalizeWords(translate('common:name')),
      // Bounded: unbounded, this split the spare width with `email` and pushed
      // the narrow columns between them apart. Matches the players grid so the
      // two roster tabs do not size differently.
      flex: 2,
      minWidth: 160,
      maxWidth: 320,
      editable: canEditInline,
      valueSetter: editCell('name', (row) => row.name),
      cellRenderer: ({ data }: CustomCellRendererProps<RosterOfficialRow>) =>
        data ? (
          <RosterMemberCell
            name={data.name}
            initials={data.initials}
            imageUrl={data.imageUrl}
          />
        ) : null,
    },
    {
      colId: 'role',
      headerName: capitalizeWords(translate('common:role')),
      // Officials have only five columns, so no single one absorbs the slack:
      // Email and Name are capped and the short columns share what is left,
      // instead of opening a gap between Email and Invite.
      flex: 1,
      minWidth: 130,
      editable: canEditInline,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: officialTypes.map((type) => String(type.id)),
      },
      // The editor stores ids; both display paths map back to the label so the
      // cell never shows a bare number.
      refData: Object.fromEntries(
        officialTypes.map((type) => [String(type.id), type.name_full ?? '-'])
      ),
      valueGetter: ({ data }: { data?: RosterOfficialRow }) =>
        data?.officialTypeId ?? '',
      valueFormatter: ({ value }: { value?: string }) =>
        roleLabel(String(value ?? '')),
      valueSetter: editCell('role', (row) => row.officialTypeId),
    },
    {
      colId: 'email',
      field: 'email',
      headerName: capitalizeWords(translate('common:email')),
      // The longest, most variable value on the row, so it gets the largest
      // share — but bounded like Name, so it cannot swallow the row.
      flex: 2,
      minWidth: 170,
      maxWidth: 320,
      // Locked once the official HAS an email — it is the invitation's
      // identity, and changing it would orphan a pending invite. The edit
      // modal locks the field under the same condition.
      editable: ({ data }: { data?: RosterOfficialRow }) =>
        canEditInline && Boolean(data) && canEditOfficialEmail(data!),
      valueSetter: editCell('email', (row) => row.email),
      valueFormatter: ({ value }: { value?: string }) => value || '-',
    },
    {
      // Named "Invite", like the players grid — it is the invite state.
      colId: 'invite',
      headerName: capitalizeWords(translate('Web:invite')),
      flex: 1,
      minWidth: 132,
      cellClass: OPERATIONS_PILL_CELL_CLASS,
      valueGetter: ({ data }: { data?: RosterOfficialRow }) =>
        data ? inviteLabel(data.inviteStatus) : '',
      cellRenderer: ({
        value,
        data,
      }: CustomCellRendererProps<RosterOfficialRow, string>) =>
        data && value ? (
          <StatusChip tone={inviteTone(data.inviteStatus)}>
            {capitalizeWords(String(value))}
          </StatusChip>
        ) : null,
    },
    {
      colId: 'phone',
      headerName: capitalizeWords(translate('common:phone')),
      flex: 1,
      minWidth: 130,
      editable: canEditInline,
      // The same country-selector input the add/edit modals use, so a number
      // cannot be entered without a country. Popup because the control is
      // taller than the row.
      cellEditor: PhoneCellEditor,
      cellEditorPopup: true,
      // Displays formatted, but edits the raw E.164 value the API stores —
      // round-tripping the formatted string would corrupt the number.
      valueGetter: ({ data }: { data?: RosterOfficialRow }) =>
        data?.phone ?? '',
      valueFormatter: ({ value }: { value?: string }) =>
        (value ? formatPhoneNumber(String(value)) : '') || '-',
      valueSetter: editCell('phone', (row) => row.phone),
    },
    ...(canEdit
      ? [
          {
            colId: 'actions',
            headerName: '',
            // Frozen to the right edge so the menu stays reachable on a wide table.
            pinned: 'right',
            width: 64,
            sortable: false,
            resizable: false,
            cellRenderer: ({
              data,
            }: CustomCellRendererProps<RosterOfficialRow>) =>
              data ? (
                renderActions ? (
                  renderActions(data)
                ) : (
                  <RosterRowActions
                    row={data}
                    onSelect={(action) => onRowAction?.(action, data)}
                    entity='teamOfficial'
                  />
                )
              ) : null,
          } as ColDef<RosterOfficialRow>,
        ]
      : []),
  ]
}
